import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ArrowLeft, Save, Plus, MinusCircle, PlusCircle, UserPlus, UserPen, AlertCircle, Pencil } from 'lucide-react'
import {
  useCliente,
  useCrearCliente,
  useActualizarCliente,
  usePaises,
  useCargos,
  useLineas,
  useLaboratorios,
  useTiposIdentificacion,
  useTiposLaboratorio,
  useGrupos,
  useCrearLaboratorio,
  useCrearCargo,
  useCrearLinea,
  useCrearTipoIdentificacion,
  useCrearTipoLaboratorioItem,
  useCrearPais,
  useCrearGrupo,
} from '../hooks/useBdClientes'
import type { LaboratorioCreatePayload } from '../types'
import { PhoneInput } from '../components/PhoneInput'
import { SearchableSelect } from '../components/SearchableSelect'

const EMAIL_REGEX = /^[^@\s]+@[a-zA-Z0-9][a-zA-Z0-9.-]{2,}\.[a-zA-Z]{2,}$/

const clienteSchema = z.object({
  nombre: z.string().min(1, 'El nombre es obligatorio').max(100),
  apellido: z.string().min(1, 'El apellido es obligatorio').max(100),
  pais_id: z.number({ required_error: 'Seleccione un país' }).min(1, 'Seleccione un país'),
  laboratorio_id: z.number({ required_error: 'Seleccione un laboratorio' }).min(1, 'Seleccione un laboratorio'),
  cargo_id: z.number({ required_error: 'Seleccione un cargo' }).min(1, 'Seleccione un cargo'),
  linea_id: z.number({ required_error: 'Seleccione una línea' }).min(1, 'Seleccione una línea'),
  marca: z.string().min(1, 'La marca es obligatoria').max(200),
  email: z.string().transform(v => {
    if (!v || v.trim() === '' || v === 'Sin correo') return 'Sin correo'
    if (!EMAIL_REGEX.test(v)) return 'Sin correo'
    return v
  }),
  estado: z.string().min(1),
  telefono_celular: z.string().nullable().optional(),
  telefono_fijo: z.string().regex(/^\d*$/, 'Solo se permiten números').nullable().optional().transform(v => v === '' ? null : v),
  direccion: z.string().nullable().optional(),
  comentarios: z.string().nullable().optional(),
  cumpleanos_dia: z.coerce.number().min(1).max(31).nullable().optional().transform(v => (v === 0 || (v !== null && v !== undefined && isNaN(v)) ? null : v)),
  cumpleanos_mes: z.coerce.number().min(1).max(12).nullable().optional().transform(v => (v === 0 || (v !== null && v !== undefined && isNaN(v)) ? null : v)),
  cumpleanos_ano: z.coerce.number().min(1).max(2100).nullable().optional().transform(v => (v === 0 || (v !== null && v !== undefined && isNaN(v)) ? null : v)),
})

type ClienteFormData = z.infer<typeof clienteSchema>

// === Modal para crear laboratorio inline ===

interface NuevoLaboratorioModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultPaisId?: number
  onCreated: (labId: number) => void
}

function NuevoLaboratorioModal({ open, onOpenChange, defaultPaisId, onCreated }: NuevoLaboratorioModalProps) {
  const { data: tiposIdentificacion } = useTiposIdentificacion()
  const { data: tiposLaboratorio } = useTiposLaboratorio()
  const { data: grupos } = useGrupos()
  const { data: paises } = usePaises(true)
  const crearMutation = useCrearLaboratorio()
  const crearTipoIdMutation = useCrearTipoIdentificacion()
  const crearTipoLabMutation = useCrearTipoLaboratorioItem()
  const crearPaisMutation = useCrearPais()
  const crearGrupoMutation = useCrearGrupo()

  const [tipoIdModalOpen, setTipoIdModalOpen] = useState(false)
  const [tipoLabModalOpen, setTipoLabModalOpen] = useState(false)
  const [paisModalOpen, setPaisModalOpen] = useState(false)
  const [grupoModalOpen, setGrupoModalOpen] = useState(false)
  const [nuevoTipoIdNombre, setNuevoTipoIdNombre] = useState('')
  const [nuevoTipoIdCodigo, setNuevoTipoIdCodigo] = useState('')
  const [nuevoTipoLabNombre, setNuevoTipoLabNombre] = useState('')
  const [nuevoPaisNombre, setNuevoPaisNombre] = useState('')
  const [nuevoPaisCodigo, setNuevoPaisCodigo] = useState('')
  const [nuevoGrupoNombre, setNuevoGrupoNombre] = useState('')

  const [form, setForm] = useState<Partial<LaboratorioCreatePayload>>({
    pais_id: defaultPaisId || 0,
  })

  useEffect(() => {
    if (open) {
      setForm({ pais_id: defaultPaisId || 0 })
    }
  }, [open, defaultPaisId])

  const handleSubmit = async () => {
    const payload = form as LaboratorioCreatePayload
    const result = await crearMutation.mutateAsync(payload)
    onCreated(result.id)
    onOpenChange(false)
  }

  const updateField = (field: keyof LaboratorioCreatePayload, value: string | number | null) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo Laboratorio</DialogTitle>
          <DialogDescription>
            Complete los datos para crear un nuevo laboratorio.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Grupo y Tipo de Identificación */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Grupo <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={grupos?.map(g => ({ value: g.id.toString(), label: g.name })) || []}
                  value={form.grupo_id ? form.grupo_id.toString() : ''}
                  onValueChange={(v) => updateField('grupo_id', Number(v))}
                  placeholder="Seleccione grupo"
                  searchPlaceholder="Buscar grupo..."
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setGrupoModalOpen(true)}
                  title="Crear nuevo grupo"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo Identificación <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={tiposIdentificacion?.map(t => ({ value: t.id.toString(), label: `${t.nombre} (${t.codigo})` })) || []}
                  value={form.tipo_identificacion_id ? form.tipo_identificacion_id.toString() : ''}
                  onValueChange={(v) => updateField('tipo_identificacion_id', Number(v))}
                  placeholder="Seleccione tipo"
                  searchPlaceholder="Buscar tipo..."
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setTipoIdModalOpen(true)}
                  title="Nuevo tipo identificación"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Identificación y Tipo Laboratorio */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Identificación <span className="text-red-500">*</span></Label>
              <Input
                value={form.identificacion || ''}
                onChange={(e) => updateField('identificacion', e.target.value)}
                placeholder="Número de identificación"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo Laboratorio <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={tiposLaboratorio?.map(t => ({ value: t.id.toString(), label: t.nombre })) || []}
                  value={form.tipo_id ? form.tipo_id.toString() : ''}
                  onValueChange={(v) => updateField('tipo_id', Number(v))}
                  placeholder="Seleccione tipo"
                  searchPlaceholder="Buscar tipo..."
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setTipoLabModalOpen(true)}
                  title="Nuevo tipo laboratorio"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Razón Social y Código ISO */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Razón Social <span className="text-red-500">*</span></Label>
              <Input
                value={form.razon_social || ''}
                onChange={(e) => updateField('razon_social', e.target.value)}
                placeholder="Razón social del laboratorio"
              />
            </div>
            <div className="space-y-2">
              <Label>Código ISO</Label>
              <Input
                value={form.codigo_iso || ''}
                onChange={(e) => updateField('codigo_iso', e.target.value || null)}
                placeholder="ISO"
              />
            </div>
          </div>

          {/* País y Dirección */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>País <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    options={paises?.map(p => ({ value: p.id.toString(), label: p.nombre })) || []}
                    value={form.pais_id ? form.pais_id.toString() : ''}
                    onValueChange={(v) => updateField('pais_id', Number(v))}
                    placeholder="Seleccione país"
                    searchPlaceholder="Buscar país..."
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={() => setPaisModalOpen(true)}
                  title="Crear nuevo país"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Teléfono Contable <span className="text-red-500">*</span></Label>
              <Input
                value={form.telefono_area_contable || ''}
                onChange={(e) => updateField('telefono_area_contable', e.target.value)}
                placeholder="Teléfono área contable"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Dirección <span className="text-red-500">*</span></Label>
            <Input
              value={form.direccion || ''}
              onChange={(e) => updateField('direccion', e.target.value)}
              placeholder="Dirección completa"
            />
          </div>

          {/* Correos */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Correo <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={form.correo || ''}
                onChange={(e) => updateField('correo', e.target.value)}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Correo Alternativo</Label>
              <Input
                type="email"
                value={form.correo_alternativo || ''}
                onChange={(e) => updateField('correo_alternativo', e.target.value || null)}
                placeholder="correo alternativo (opcional)"
              />
            </div>
          </div>

          {/* Comentario */}
          <div className="space-y-2">
            <Label>Comentario</Label>
            <Textarea
              value={form.comentario || ''}
              onChange={(e) => updateField('comentario', e.target.value || null)}
              placeholder="Comentarios adicionales (opcional)"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={crearMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            {crearMutation.isPending ? 'Creando...' : 'Crear Laboratorio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Modal Nuevo Tipo Identificación */}
    <Dialog open={tipoIdModalOpen} onOpenChange={setTipoIdModalOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Nuevo Tipo de Identificación</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre <span className="text-red-500">*</span></Label>
            <Input
              value={nuevoTipoIdNombre}
              onChange={(e) => setNuevoTipoIdNombre(e.target.value)}
              placeholder="Ej: Cédula de Identidad"
            />
          </div>
          <div className="space-y-2">
            <Label>Código <span className="text-red-500">*</span></Label>
            <Input
              value={nuevoTipoIdCodigo}
              onChange={(e) => setNuevoTipoIdCodigo(e.target.value)}
              placeholder="Ej: CI"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setTipoIdModalOpen(false); setNuevoTipoIdNombre(''); setNuevoTipoIdCodigo('') }}>
            Cancelar
          </Button>
          <Button
            disabled={!nuevoTipoIdNombre.trim() || !nuevoTipoIdCodigo.trim() || crearTipoIdMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
            onClick={async () => {
              const result = await crearTipoIdMutation.mutateAsync({ nombre: nuevoTipoIdNombre.trim(), codigo: nuevoTipoIdCodigo.trim(), activo: true })
              updateField('tipo_identificacion_id', result.id)
              setNuevoTipoIdNombre('')
              setNuevoTipoIdCodigo('')
              setTipoIdModalOpen(false)
            }}
          >
            {crearTipoIdMutation.isPending ? 'Creando...' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Modal Nuevo Tipo Laboratorio */}
    <Dialog open={tipoLabModalOpen} onOpenChange={setTipoLabModalOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Nuevo Tipo de Laboratorio</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre <span className="text-red-500">*</span></Label>
            <Input
              value={nuevoTipoLabNombre}
              onChange={(e) => setNuevoTipoLabNombre(e.target.value)}
              placeholder="Nombre del tipo"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && nuevoTipoLabNombre.trim()) {
                  e.preventDefault()
                  crearTipoLabMutation.mutateAsync({ nombre: nuevoTipoLabNombre.trim(), activo: true }).then((result) => {
                    updateField('tipo_id', result.id)
                    setNuevoTipoLabNombre('')
                    setTipoLabModalOpen(false)
                  })
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setTipoLabModalOpen(false); setNuevoTipoLabNombre('') }}>
            Cancelar
          </Button>
          <Button
            disabled={!nuevoTipoLabNombre.trim() || crearTipoLabMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
            onClick={async () => {
              const result = await crearTipoLabMutation.mutateAsync({ nombre: nuevoTipoLabNombre.trim(), activo: true })
              updateField('tipo_id', result.id)
              setNuevoTipoLabNombre('')
              setTipoLabModalOpen(false)
            }}
          >
            {crearTipoLabMutation.isPending ? 'Creando...' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Modal Nuevo País */}
    <Dialog open={paisModalOpen} onOpenChange={setPaisModalOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Nuevo País</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre <span className="text-red-500">*</span></Label>
            <Input
              value={nuevoPaisNombre}
              onChange={(e) => setNuevoPaisNombre(e.target.value)}
              placeholder="Ej: Ecuador"
            />
          </div>
          <div className="space-y-2">
            <Label>Código</Label>
            <Input
              value={nuevoPaisCodigo}
              onChange={(e) => setNuevoPaisCodigo(e.target.value)}
              placeholder="Ej: EC"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setPaisModalOpen(false); setNuevoPaisNombre(''); setNuevoPaisCodigo('') }}>
            Cancelar
          </Button>
          <Button
            disabled={!nuevoPaisNombre.trim() || crearPaisMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
            onClick={async () => {
              const result = await crearPaisMutation.mutateAsync({ nombre: nuevoPaisNombre.trim(), codigo: nuevoPaisCodigo.trim() || null, activo: true })
              updateField('pais_id', result.id)
              setNuevoPaisNombre('')
              setNuevoPaisCodigo('')
              setPaisModalOpen(false)
            }}
          >
            {crearPaisMutation.isPending ? 'Creando...' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Modal Nuevo Grupo */}
    <Dialog open={grupoModalOpen} onOpenChange={setGrupoModalOpen}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Nuevo Grupo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre <span className="text-red-500">*</span></Label>
            <Input
              value={nuevoGrupoNombre}
              onChange={(e) => setNuevoGrupoNombre(e.target.value)}
              placeholder="Nombre del grupo"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && nuevoGrupoNombre.trim()) {
                  e.preventDefault()
                  crearGrupoMutation.mutateAsync({ name: nuevoGrupoNombre.trim() }).then((result) => {
                    updateField('grupo_id', result.id)
                    setNuevoGrupoNombre('')
                    setGrupoModalOpen(false)
                  })
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setGrupoModalOpen(false); setNuevoGrupoNombre('') }}>
            Cancelar
          </Button>
          <Button
            disabled={!nuevoGrupoNombre.trim() || crearGrupoMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
            onClick={async () => {
              const result = await crearGrupoMutation.mutateAsync({ name: nuevoGrupoNombre.trim() })
              updateField('grupo_id', result.id)
              setNuevoGrupoNombre('')
              setGrupoModalOpen(false)
            }}
          >
            {crearGrupoMutation.isPending ? 'Creando...' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}

export function ClienteFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id
  const clienteId = Number(id)

  const { data: cliente, isLoading: clienteLoading } = useCliente(isEditing ? clienteId : 0)
  const crearMutation = useCrearCliente()
  const actualizarMutation = useActualizarCliente()
  const alertRef = useRef<HTMLDivElement>(null)
  const [backendError, setBackendError] = useState<string | null>(null)
  const [emailEditable, setEmailEditable] = useState(false)

  const { data: paises } = usePaises(true)
  const { data: cargos } = useCargos(undefined, true)
  const { data: lineas } = useLineas(true)

  useDocumentTitle(isEditing ? 'Editar Cliente' : 'Nuevo Cliente')

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ClienteFormData>({
    resolver: zodResolver(clienteSchema),
    defaultValues: {
      email: '',
      estado: 'activo',
      pais_id: 0,
      laboratorio_id: 0,
      cargo_id: 0,
      linea_id: 0,
      cumpleanos_dia: null,
      cumpleanos_mes: null,
      cumpleanos_ano: null,
    },
  })

  // Auto-seleccionar "Sin cargo" y "Sin línea" por defecto (solo una vez)
  const cargoDefaultSet = useRef(false)
  const lineaDefaultSet = useRef(false)

  useEffect(() => {
    if (cargos && !isEditing && !cargoDefaultSet.current) {
      const sinCargo = cargos.find(c => c.nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes('sin cargo'))
      if (sinCargo) {
        setValue('cargo_id', sinCargo.id)
        cargoDefaultSet.current = true
      }
    }
  }, [cargos, isEditing, setValue])

  useEffect(() => {
    if (lineas && !isEditing && !lineaDefaultSet.current) {
      const sinLinea = lineas.find(l => l.nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes('sin linea'))
      if (sinLinea) {
        setValue('linea_id', sinLinea.id)
        lineaDefaultSet.current = true
      }
    }
  }, [lineas, isEditing, setValue])

  const paisId = watch('pais_id')
  const telefonoCelular = watch('telefono_celular')
  const { data: laboratorios } = useLaboratorios(paisId > 0 ? paisId : undefined)

  const [labModalOpen, setLabModalOpen] = useState(false)
  const [cargoModalOpen, setCargoModalOpen] = useState(false)
  const [lineaModalOpen, setLineaModalOpen] = useState(false)
  const [paisModalOpen, setPaisModalOpen] = useState(false)
  const [nuevoCargoNombre, setNuevoCargoNombre] = useState('')
  const [nuevaLineaNombre, setNuevaLineaNombre] = useState('')
  const [nuevoPaisNombre, setNuevoPaisNombre] = useState('')
  const [nuevoPaisCodigo, setNuevoPaisCodigo] = useState('')
  const crearCargoMutation = useCrearCargo()
  const crearLineaMutation = useCrearLinea()
  const crearPaisMutation = useCrearPais()

  // Obtener código de país para PhoneInput
  const paisSeleccionado = paises?.find(p => p.id === paisId)
  const paisCodigo = paisSeleccionado?.codigo || null

  // Cargar datos del cliente al editar
  useEffect(() => {
    if (cliente && isEditing) {
      const tieneEmail = cliente.email && cliente.email !== 'Sin correo'
      reset({
        nombre: cliente.nombre,
        apellido: cliente.apellido,
        pais_id: cliente.pais_id,
        laboratorio_id: cliente.laboratorio_id,
        cargo_id: cliente.cargo_id,
        linea_id: cliente.linea_id,
        marca: cliente.marca,
        email: tieneEmail ? cliente.email : '',
        estado: cliente.estado,
        telefono_celular: cliente.telefono_celular,
        telefono_fijo: cliente.telefono_fijo,
        direccion: cliente.direccion,
        comentarios: cliente.comentarios,
        cumpleanos_dia: cliente.cumpleanos_dia,
        cumpleanos_mes: cliente.cumpleanos_mes,
        cumpleanos_ano: cliente.cumpleanos_ano,
      })
      if (tieneEmail) setEmailEditable(true)
    }
  }, [cliente, isEditing, reset])

  // Mapa de nombres de campos para mostrar en la alerta
  const fieldLabels: Record<string, string> = {
    nombre: 'Nombre',
    apellido: 'Apellido',
    email: 'Email',
    pais_id: 'País',
    laboratorio_id: 'Laboratorio',
    cargo_id: 'Cargo',
    linea_id: 'Línea',
    marca: 'Marca',
    estado: 'Estado',
    telefono_celular: 'Teléfono Celular',
    telefono_fijo: 'Teléfono Fijo',
    direccion: 'Dirección',
    cumpleanos_dia: 'Día de cumpleaños',
    cumpleanos_mes: 'Mes de cumpleaños',
    cumpleanos_ano: 'Año de cumpleaños',
  }

  const onInvalid = () => {
    setBackendError(null)
    setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
  }

  const onSubmit = async (data: ClienteFormData) => {
    setBackendError(null)
    // Si solo tiene prefijo sin dígitos, enviar null
    const telLimpio = data.telefono_celular?.replace(/\D/g, '')
    const payload = {
      ...data,
      telefono_celular: telLimpio && telLimpio.length > 3 ? (data.telefono_celular || '') : '',
      direccion: data.direccion || '',
      telefono_fijo: data.telefono_fijo || null,
      comentarios: data.comentarios || null,
      cumpleanos_dia: data.cumpleanos_dia ?? null,
      cumpleanos_mes: data.cumpleanos_mes ?? null,
      cumpleanos_ano: data.cumpleanos_ano ?? null,
    }

    try {
      if (isEditing) {
        await actualizarMutation.mutateAsync({ id: clienteId, payload })
        navigate(`/bd-clientes/${clienteId}`)
      } else {
        const result = await crearMutation.mutateAsync(payload)
        navigate(`/bd-clientes/${result.id}`)
      }
    } catch (err: unknown) {
      // Extraer mensaje de error del backend
      let errorMsg = 'Error al guardar el cliente'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { error?: string; detail?: Array<{ loc?: string[]; msg?: string }> | string } } }
        const respData = axiosErr.response?.data
        if (respData?.detail) {
          if (Array.isArray(respData.detail)) {
            // Errores de validación Pydantic (422)
            const campos = respData.detail.map(d => {
              const campo = d.loc?.filter(l => l !== 'body' && l !== 'payload')?.join('.') || 'campo'
              const label = fieldLabels[campo] || campo
              // Limpiar prefijos técnicos de Pydantic
              const msg = (d.msg || 'Campo inválido').replace(/^Value error,\s*/i, '')
              return `${label}: ${msg}`
            })
            errorMsg = campos.join('\n')
          } else {
            errorMsg = String(respData.detail)
          }
        } else if (respData?.error) {
          errorMsg = respData.error
        }
      }
      setBackendError(errorMsg)
      setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50)
    }
  }

  if (isEditing && clienteLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    )
  }

  const meses = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' }, { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' }, { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          {isEditing ? <UserPen className="h-6 w-6" /> : <UserPlus className="h-6 w-6" />}
          {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {/* Alerta de errores de validación */}
        {(Object.keys(errors).length > 0 || backendError) && (
          <Alert ref={alertRef} variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {backendError ? 'Error al guardar' : 'Campos con errores'}
            </AlertTitle>
            <AlertDescription>
              {backendError ? (
                <p className="whitespace-pre-line">{backendError}</p>
              ) : (
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  {Object.entries(errors).map(([key, err]) => (
                    <li key={key}>
                      <span className="font-medium">{fieldLabels[key] || key}</span>: {err?.message || 'Campo inválido'}
                    </li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Información básica */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Información Personal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre <span className="text-red-500">*</span></Label>
                <Input {...register('nombre')} placeholder="Nombre" />
                {errors.nombre && <p className="text-sm text-red-500">{errors.nombre.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Apellido <span className="text-red-500">*</span></Label>
                <Input {...register('apellido')} placeholder="Apellido" />
                {errors.apellido && <p className="text-sm text-red-500">{errors.apellido.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Input
                    {...register('email')}
                    type="email"
                    placeholder="Sin correo"
                    disabled={!emailEditable}
                    className={!emailEditable ? 'pr-10 bg-muted' : 'pr-10'}
                  />
                  {!emailEditable && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => {
                        setEmailEditable(true)
                        setTimeout(() => {
                          const emailInput = document.querySelector('input[name="email"]') as HTMLInputElement
                          emailInput?.focus()
                        }, 0)
                      }}
                      title="Editar email"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={watch('estado')}
                  onValueChange={(v) => setValue('estado', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                    <SelectItem value="prospecto">Por validar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clasificación */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Clasificación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>País <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      options={paises?.map(p => ({ value: p.id.toString(), label: p.nombre })) || []}
                      value={paisId > 0 ? paisId.toString() : ''}
                      onValueChange={(v) => {
                        setValue('pais_id', Number(v))
                        setValue('laboratorio_id', 0)
                      }}
                      placeholder="Seleccione país"
                      searchPlaceholder="Buscar país..."
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setPaisModalOpen(true)}
                    title="Crear nuevo país"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {errors.pais_id && <p className="text-sm text-red-500">{errors.pais_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Laboratorio <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      options={laboratorios?.map(l => ({ value: l.id.toString(), label: l.razon_social })) || []}
                      value={watch('laboratorio_id') > 0 ? watch('laboratorio_id').toString() : ''}
                      onValueChange={(v) => setValue('laboratorio_id', Number(v))}
                      disabled={!paisId || paisId === 0}
                      placeholder={paisId > 0 ? 'Seleccione laboratorio' : 'Seleccione país primero'}
                      searchPlaceholder="Buscar laboratorio..."
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setLabModalOpen(true)}
                    disabled={!paisId || paisId === 0}
                    title="Crear nuevo laboratorio"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {errors.laboratorio_id && <p className="text-sm text-red-500">{errors.laboratorio_id.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Cargo <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      options={cargos?.map(c => ({ value: c.id.toString(), label: c.nombre })) || []}
                      value={watch('cargo_id') > 0 ? watch('cargo_id').toString() : ''}
                      onValueChange={(v) => {
                        if (v) {
                          setValue('cargo_id', Number(v))
                        } else {
                          const sinCargo = cargos?.find(c => c.nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes('sin cargo'))
                          setValue('cargo_id', sinCargo?.id || 0)
                        }
                      }}
                      placeholder="Seleccione cargo"
                      searchPlaceholder="Buscar cargo..."
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setCargoModalOpen(true)}
                    title="Crear nuevo cargo"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {errors.cargo_id && <p className="text-sm text-red-500">{errors.cargo_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Línea <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      options={lineas?.map(l => ({ value: l.id.toString(), label: l.nombre })) || []}
                      value={watch('linea_id') > 0 ? watch('linea_id').toString() : ''}
                      onValueChange={(v) => {
                        if (v) {
                          setValue('linea_id', Number(v))
                        } else {
                          const sinLinea = lineas?.find(l => l.nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes('sin linea'))
                          setValue('linea_id', sinLinea?.id || 0)
                        }
                      }}
                      placeholder="Seleccione línea"
                      searchPlaceholder="Buscar línea..."
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setLineaModalOpen(true)}
                    title="Crear nueva línea"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {errors.linea_id && <p className="text-sm text-red-500">{errors.linea_id.message}</p>}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label>Marca <span className="text-red-500">*</span></Label>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title={watch('marca') === 'Sin marca' ? 'Quitar "Sin marca"' : 'Sin marca'}
                    onClick={() => {
                      if (watch('marca') === 'Sin marca') {
                        setValue('marca', '')
                      } else {
                        setValue('marca', 'Sin marca')
                      }
                    }}
                  >
                    {watch('marca') === 'Sin marca' ? (
                      <MinusCircle className="h-3.5 w-3.5" />
                    ) : (
                      <PlusCircle className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                <Input {...register('marca')} placeholder="Marca asociada" />
                {errors.marca && <p className="text-sm text-red-500">{errors.marca.message}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PhoneInput
                value={telefonoCelular || ''}
                onChange={(v) => setValue('telefono_celular', v)}
                paisCodigo={paisCodigo}
              />
              <div className="space-y-2">
                <Label>Teléfono Fijo</Label>
                <Input
                  {...register('telefono_fijo')}
                  placeholder="Teléfono fijo (opcional)"
                  inputMode="numeric"
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement
                    target.value = target.value.replace(/\D/g, '')
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cumpleaños */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Cumpleaños (opcional)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Día</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  placeholder="Día"
                  {...register('cumpleanos_dia', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label>Mes</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  {...register('cumpleanos_mes', { valueAsNumber: true })}
                >
                  <option value="">Mes</option>
                  {meses.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Año (opcional)</Label>
                <Input
                  type="number"
                  min={1900}
                  max={2100}
                  placeholder="Año"
                  {...register('cumpleanos_ano', { valueAsNumber: true })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comentarios */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Comentarios</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register('comentarios')}
              placeholder="Comentarios adicionales..."
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Acciones */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || crearMutation.isPending || actualizarMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? 'Guardar Cambios' : 'Crear Cliente'}
          </Button>
        </div>
      </form>

      <NuevoLaboratorioModal
        open={labModalOpen}
        onOpenChange={setLabModalOpen}
        defaultPaisId={paisId > 0 ? paisId : undefined}
        onCreated={(labId) => setValue('laboratorio_id', labId)}
      />

      {/* Modal Nuevo Cargo */}
      <Dialog open={cargoModalOpen} onOpenChange={setCargoModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Nuevo Cargo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre <span className="text-red-500">*</span></Label>
              <Input
                value={nuevoCargoNombre}
                onChange={(e) => setNuevoCargoNombre(e.target.value)}
                placeholder="Nombre del cargo"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nuevoCargoNombre.trim()) {
                    e.preventDefault()
                    crearCargoMutation.mutateAsync({ nombre: nuevoCargoNombre.trim(), activo: true }).then((result) => {
                      setValue('cargo_id', result.id)
                      setNuevoCargoNombre('')
                      setCargoModalOpen(false)
                    })
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCargoModalOpen(false); setNuevoCargoNombre('') }}>
              Cancelar
            </Button>
            <Button
              disabled={!nuevoCargoNombre.trim() || crearCargoMutation.isPending}
              className="bg-teal-800 hover:bg-teal-900 text-white"
              onClick={async () => {
                const result = await crearCargoMutation.mutateAsync({ nombre: nuevoCargoNombre.trim(), activo: true })
                setValue('cargo_id', result.id)
                setNuevoCargoNombre('')
                setCargoModalOpen(false)
              }}
            >
              {crearCargoMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nuevo País */}
      <Dialog open={paisModalOpen} onOpenChange={setPaisModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Nuevo País</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre <span className="text-red-500">*</span></Label>
              <Input
                value={nuevoPaisNombre}
                onChange={(e) => setNuevoPaisNombre(e.target.value)}
                placeholder="Ej: Ecuador"
              />
            </div>
            <div className="space-y-2">
              <Label>Código</Label>
              <Input
                value={nuevoPaisCodigo}
                onChange={(e) => setNuevoPaisCodigo(e.target.value)}
                placeholder="Ej: EC"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPaisModalOpen(false); setNuevoPaisNombre(''); setNuevoPaisCodigo('') }}>
              Cancelar
            </Button>
            <Button
              disabled={!nuevoPaisNombre.trim() || crearPaisMutation.isPending}
              className="bg-teal-800 hover:bg-teal-900 text-white"
              onClick={async () => {
                const result = await crearPaisMutation.mutateAsync({ nombre: nuevoPaisNombre.trim(), codigo: nuevoPaisCodigo.trim() || null, activo: true })
                setValue('pais_id', result.id)
                setValue('laboratorio_id', 0)
                setNuevoPaisNombre('')
                setNuevoPaisCodigo('')
                setPaisModalOpen(false)
              }}
            >
              {crearPaisMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nueva Línea */}
      <Dialog open={lineaModalOpen} onOpenChange={setLineaModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Nueva Línea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre <span className="text-red-500">*</span></Label>
              <Input
                value={nuevaLineaNombre}
                onChange={(e) => setNuevaLineaNombre(e.target.value)}
                placeholder="Nombre de la línea"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nuevaLineaNombre.trim()) {
                    e.preventDefault()
                    crearLineaMutation.mutateAsync({ nombre: nuevaLineaNombre.trim(), activo: true }).then((result) => {
                      setValue('linea_id', result.id)
                      setNuevaLineaNombre('')
                      setLineaModalOpen(false)
                    })
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLineaModalOpen(false); setNuevaLineaNombre('') }}>
              Cancelar
            </Button>
            <Button
              disabled={!nuevaLineaNombre.trim() || crearLineaMutation.isPending}
              className="bg-teal-800 hover:bg-teal-900 text-white"
              onClick={async () => {
                const result = await crearLineaMutation.mutateAsync({ nombre: nuevaLineaNombre.trim(), activo: true })
                setValue('linea_id', result.id)
                setNuevaLineaNombre('')
                setLineaModalOpen(false)
              }}
            >
              {crearLineaMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
